import React, { useState, useEffect } from 'react';
import SelectInput from '../FormElements/SelectInput.jsx';
import TextInput from '../FormElements/TextInput.jsx';
import CheckboxInput from '../FormElements/CheckboxInput.jsx';

function MultiSelectWithOther(props) {
    const { onChange: driverOnChange, schema, value, ...rest } = props;
    const { validation, errorMessages, componentProps, otherInputConfig = {} } = schema;

    // For WithOther types, value is expected to be an object: { selections: [...], otherDetail: ... }
    // Extract the selections array for the select input
    let selections = [];
    let otherDetail = "";
    let arrKey = "selections";
    let strKey = "otherDetail";
    if (typeof value === "object" && value !== null) {
        arrKey = Object.keys(value).find(k => Array.isArray(value[k])) || "selections";
        selections = value[arrKey] || [];
        strKey = Object.keys(value).find(k => typeof value[k] === "string" && k !== arrKey) || "otherDetail";
        otherDetail = value[strKey] || "";
    } else if (Array.isArray(value)) {
        selections = value;
    }

    // "Other" config
    const {
        optionValue: otherOptionValue = "other",
        optionLabel: otherOptionLabel = "Otro",
        textInputPlaceholder,
        textInputLabel,
        textInputWrapperClassName,
        placeholder: legacyOtherPlaceholder
    } = otherInputConfig;
    const otherPlaceholder = textInputPlaceholder || legacyOtherPlaceholder || "Please specify";

    // Only standard options in the select (no "Other")
    let options = Array.isArray(schema.options)
        ? schema.options.filter(opt => opt.value !== otherOptionValue)
        : [];

    // For checkbox styling
    const questionId = schema.id || rest.id || "multiSelectWithOther";
    const currentSelections = selections;
    const checkboxWrapperClassName = componentProps?.wrapperClassName || "form-check mb-2 mt-3";
    const checkboxLabelClassName = componentProps?.labelClassName || "ms-2";
    // If you have sections, you could check for "other" in them, but for now:
    const hasOtherInSections = false;
    const handleCheckboxChange = (optionValue, isChecked) => {
        let newSelections = [...currentSelections];
        if (isChecked) {
            if (!newSelections.includes(optionValue)) {
                newSelections.push(optionValue);
            }
        } else {
            newSelections = newSelections.filter(v => v !== optionValue);
        }
        // If unchecking, also clear otherDetail
        validateAndReport(newSelections, isChecked ? otherDetail : "");
    };

    const [internalError, setInternalError] = useState('');

    const hasOtherSelected = selections.includes(otherOptionValue);

    useEffect(() => {
        if (!hasOtherSelected && internalError) {
            setInternalError('');
        }
        if (hasOtherSelected && otherDetail.trim() === '' && validation?.otherInputRequired) {
            validateAndReport(selections, otherDetail, true);
        } else if (hasOtherSelected && otherDetail.trim() !== '') {
            setInternalError('');
        }
    }, [hasOtherSelected, otherDetail, internalError, validation?.otherInputRequired]);

    const validateAndReport = (newSelections, newOtherDetail, isInitialCheck = false) => {
        let error = '';
        const finalOtherDetail = newSelections.includes(otherOptionValue) ? newOtherDetail : '';

        if (validation?.required && (!newSelections || newSelections.length === 0)) {
            error = errorMessages?.required || "Selection is required.";
        }
        if (!error && validation?.minSelections && newSelections.length < validation.minSelections) {
            error = errorMessages?.minSelections || `Please select at least ${validation.minSelections} option(s).`;
        }
        if (!error && validation?.maxSelections && newSelections.length > validation.maxSelections) {
            error = errorMessages?.maxSelections || `Please select no more than ${validation.maxSelections} option(s).`;
        }
        if (!error && newSelections.includes(otherOptionValue) && validation?.otherInputRequired && finalOtherDetail.trim() === '') {
            error = errorMessages?.otherInputRequired || "Please specify details for 'Other'.";
        }
        if (!error && newSelections.includes(otherOptionValue) && finalOtherDetail.trim() !== '' && validation?.otherInputMinLength && finalOtherDetail.length < validation.otherInputMinLength) {
            error = errorMessages?.otherInputMinLength || `Other details must be at least ${validation.otherInputMinLength} characters.`;
        }
        if (!error && newSelections.includes(otherOptionValue) && finalOtherDetail.trim() !== '' && validation?.otherInputMaxLength && finalOtherDetail.length > validation.otherInputMaxLength) {
            error = errorMessages?.otherInputMaxLength || `Other details must not exceed ${validation.otherInputMaxLength} characters.`;
        }

        setInternalError(error);

        // Reconstruct the answer object, preserving keys
        let newValue;
        if (typeof value === "object" && value !== null) {
            newValue = { ...value, [arrKey]: newSelections, [strKey]: finalOtherDetail };
        } else {
            newValue = { selections: newSelections, otherDetail: finalOtherDetail };
        }

        driverOnChange(newValue, error);
    };

    const handleChange = (event) => {
        const newSelections = event.target.value;
        validateAndReport(newSelections, otherDetail);
    };

    const handleOtherInputChange = (event) => {
        const newOtherDetail = event.target.value;
        validateAndReport(selections, newOtherDetail);
    };

    return (
        <div>
            <SelectInput
                {...rest}
                {...componentProps}
                value={selections.filter(v => v !== otherOptionValue)}
                onChange={e => {
                    // Only update selections for standard options
                    const newSelections = e.target.value.filter(v => v !== otherOptionValue);
                    // If "Other" was previously selected, keep it checked
                    const mergedSelections = hasOtherSelected
                        ? Array.from(new Set([...newSelections, otherOptionValue]))
                        : newSelections;
                    validateAndReport(mergedSelections, otherDetail);
                }}
                multiple={true}
            />
            {/* Always render the "Other" checkbox if otherInputConfig is present and not already in any section */}
            {Object.keys(otherInputConfig).length > 0 && otherOptionValue && !hasOtherInSections && (
                <CheckboxInput
                    key="other-checkbox"
                    id={`${questionId}-${otherOptionValue}`}
                    name={questionId}
                    value={otherOptionValue}
                    checked={currentSelections.includes(otherOptionValue)}
                    onChange={(e) => handleCheckboxChange(otherOptionValue, e.target.checked)}
                    label={otherOptionLabel}
                    wrapperClassName={checkboxWrapperClassName}
                    labelClassName={checkboxLabelClassName}
                />
            )}
            {/* Show the text input if "Other" is checked */}
            {Object.keys(otherInputConfig).length > 0 && otherOptionValue && hasOtherSelected && (
                <div className={textInputWrapperClassName ? textInputWrapperClassName + " mt-4" : "mt-4"}>
                    {textInputLabel && (
                        <label htmlFor={`${rest.id || schema.id || "multiSelectWithOther"}-otherDetail`} className="form-label">
                            {textInputLabel}
                        </label>
                    )}
                    <TextInput
                        id={`${rest.id || schema.id || "multiSelectWithOther"}-otherDetail`}
                        name={`${rest.id || schema.id || "multiSelectWithOther"}-otherDetail`}
                        type="text"
                        value={otherDetail}
                        onChange={handleOtherInputChange}
                        placeholder={otherPlaceholder}
                        inputClassName={componentProps?.otherInputClassName || "form-control"}
                        ariaLabel={textInputLabel || "Other specification"}
                        noWrapper={true}
                    />
                    {internalError && validation?.otherInputRequired && (
                        <div className="text-danger mt-1 ms-1" style={{ fontSize: '0.875em' }}>
                            {textInputLabel
                                ? `Obligatorio`
                                : internalError
                            }
                        </div>
                    )}
                </div>
            )}
            {!hasOtherSelected && internalError && (
                <div className="text-danger mt-3 ms-1" style={{ fontSize: '0.875em' }}>
                    {internalError}
                </div>
            )}
        </div>
    );
}

export default MultiSelectWithOther;